import { useEffect, useMemo } from 'react';
import { useNotify } from 'react-admin';
import { JobPhaseReceivableSheetInterface } from '@vatos-pas/common';

import { toNumber } from 'utils/currency';
import { validateInvoice } from '../utils/validateInvoice';
import {
  formatPOAmountForInput,
  parseInvoiceNumber,
  parsePOAmount,
} from '../utils/invoiceInput';
import { useOptimisticReceivable } from './useOptimisticReceivable';
import {
  InvoiceDraft,
  InvoiceFieldErrors,
  useInvoiceDraftRegistry,
} from './InvoiceDraftRegistry';

const seedFromRecord = (
  record: JobPhaseReceivableSheetInterface | undefined,
): InvoiceDraft => ({
  poAmountInput: formatPOAmountForInput(record?.invoicePOAmount ?? null),
  invoiceNumberInput: record?.internalInvoiceNumber ?? '',
  committedPO: toNumber(record?.invoicePOAmount ?? null),
  committedInvoice: record?.internalInvoiceNumber ?? null,
  errors: {},
  submitting: false,
});

// Derived field state shared by the read and save hooks, so the inputs and the
// Save button always agree on what changed.
const deriveChanges = (
  draft: InvoiceDraft,
): {
  parsedPOAmount: number | null;
  parsedInvoiceNumber: string | null;
  poChanged: boolean;
  invoiceChanged: boolean;
  hasChanges: boolean;
} => {
  const parsedPOAmount = parsePOAmount(draft.poAmountInput);
  const parsedInvoiceNumber = parseInvoiceNumber(draft.invoiceNumberInput);
  const poChanged = parsedPOAmount !== draft.committedPO;
  const invoiceChanged = parsedInvoiceNumber !== draft.committedInvoice;
  return {
    parsedPOAmount,
    parsedInvoiceNumber,
    poChanged,
    invoiceChanged,
    hasChanges: poChanged || invoiceChanged,
  };
};

const hasExistingValues = (draft: InvoiceDraft): boolean =>
  draft.committedPO !== null ||
  (draft.committedInvoice !== null && draft.committedInvoice !== '');

// Dirty = a field diverges from its committed baseline, or an error is showing.
// Guards the resync effect so an external refetch can't discard unsaved edits.
const isDirty = (draft: InvoiceDraft): boolean =>
  deriveChanges(draft).hasChanges || Object.keys(draft.errors).length > 0;

// The reactive draft read falls back to a pre-seed value so a cell rendering
// before the seed effect runs still shows the record's values.
const useDraftAccess = (
  record: JobPhaseReceivableSheetInterface | undefined,
) => {
  const registry = useInvoiceDraftRegistry();
  const jobPhaseId = record?.jobPhaseId ?? '';
  const seed = useMemo(() => seedFromRecord(record), [record]);
  const draft =
    (jobPhaseId ? registry.getDraft(jobPhaseId) : undefined) ?? seed;
  return { registry, jobPhaseId, seed, draft };
};

type InvoiceDraftState = {
  poAmountInput: string;
  invoiceNumberInput: string;
  errors: InvoiceFieldErrors;
  submitting: boolean;
  setPoAmountInput: (next: string) => void;
  setInvoiceNumberInput: (next: string) => void;
};

/**
 * Read/edit half of the split invoice cells (PO Amount / Invoice #). Owns the
 * single seed/resync effect and exposes the controlled values + setters.
 * Deliberately free of the commit/persistence/notify stack — only the Save cell
 * needs that (see useInvoiceSave).
 *
 * Both input cells call this with the same record; the effect's record-reference
 * dedupe collapses their two seed writes to one. `editable` gates the effect so
 * read-only cells never touch the store.
 */
export const useInvoiceDraft = (
  record: JobPhaseReceivableSheetInterface | undefined,
  editable: boolean,
): InvoiceDraftState => {
  const { registry, jobPhaseId, seed, draft } = useDraftAccess(record);
  const { setDraft, syncFromRecord, markSynced, peekDraft, pendingRef } =
    registry;

  useEffect(() => {
    if (!editable || !record || !jobPhaseId) return;
    if (pendingRef.current[jobPhaseId]) {
      // A refetch landed during our own save: consume the incoming record so a
      // later pass can't reseed over the baseline handleSave is about to adopt.
      markSynced(jobPhaseId, record);
      return;
    }
    // Don't clobber an in-progress unsaved edit: only reseed a clean draft.
    const current = peekDraft(jobPhaseId);
    if (current && isDirty(current)) return;
    syncFromRecord(jobPhaseId, record, seed);
  }, [
    record,
    jobPhaseId,
    editable,
    seed,
    syncFromRecord,
    markSynced,
    peekDraft,
    pendingRef,
  ]);

  const setPoAmountInput = (next: string) =>
    setDraft(jobPhaseId, { poAmountInput: next });
  const setInvoiceNumberInput = (next: string) =>
    setDraft(jobPhaseId, { invoiceNumberInput: next });

  return {
    poAmountInput: draft.poAmountInput,
    invoiceNumberInput: draft.invoiceNumberInput,
    errors: draft.errors,
    submitting: draft.submitting,
    setPoAmountInput,
    setInvoiceNumberInput,
  };
};

type InvoiceSaveState = {
  submitting: boolean;
  hasChanges: boolean;
  hasExistingValues: boolean;
  handleSave: () => Promise<void>;
};

/**
 * Save half of the split invoice cells: hosts the single shared Save/Update
 * button's commit. Pulls the optimistic commit core + notify here so the input
 * cells don't, building the persistence stack once per row.
 *
 * Reads the same shared draft as useInvoiceDraft, so the button label/enabled
 * state stay in lockstep with the inputs. `commit` (via the receivable registry)
 * still guarantees a single create when both fields are saved on a new row.
 *
 * Note: useOptimisticReceivable's own per-instance `pendingRef` is intentionally
 * unused here — the resync effect (in useInvoiceDraft) is guarded by the
 * row-keyed `registry.pendingRef`, which is shared across the row's cells.
 */
export const useInvoiceSave = (
  record: JobPhaseReceivableSheetInterface | undefined,
): InvoiceSaveState => {
  const notify = useNotify();
  const { registry, jobPhaseId, draft } = useDraftAccess(record);
  const { commit } = useOptimisticReceivable(record);

  const {
    parsedPOAmount,
    parsedInvoiceNumber,
    poChanged,
    invoiceChanged,
    hasChanges,
  } = deriveChanges(draft);

  const handleSave = async () => {
    const validationErrors = validateInvoice({
      poAmount: parsedPOAmount,
      invoiceNumber: parsedInvoiceNumber,
    });

    if (validationErrors.length > 0) {
      const nextErrors: InvoiceFieldErrors = {};
      validationErrors.forEach(err => {
        nextErrors[err.field] = err.message;
      });
      registry.setDraft(jobPhaseId, { errors: nextErrors });
      return;
    }

    registry.setDraft(jobPhaseId, { errors: {}, submitting: true });
    registry.pendingRef.current[jobPhaseId] = true;
    try {
      await commit({
        ...(poChanged ? { invoicePOAmount: parsedPOAmount } : {}),
        ...(invoiceChanged
          ? { internalInvoiceNumber: parsedInvoiceNumber }
          : {}),
      });
      // Adopt the saved values as the new baseline immediately so the button
      // flips to "Update" / no-changes right away.
      registry.setDraft(jobPhaseId, {
        poAmountInput: formatPOAmountForInput(parsedPOAmount),
        invoiceNumberInput: parsedInvoiceNumber ?? '',
        committedPO: parsedPOAmount,
        committedInvoice: parsedInvoiceNumber,
        errors: {},
        submitting: false,
      });
      notify('Changes were made successfully!');
    } catch (error) {
      registry.setDraft(jobPhaseId, { submitting: false });
      const message =
        error instanceof Error ? error.message : 'An error has occurred!';
      notify(message, 'warning');
    } finally {
      registry.pendingRef.current[jobPhaseId] = false;
    }
  };

  return {
    submitting: draft.submitting,
    hasChanges,
    hasExistingValues: hasExistingValues(draft),
    handleSave,
  };
};
